import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";


// ==========================================
// NUMBER TO WORDS
// ==========================================

const numberToWords = (number) => {

  number = Number(number);

  if (!Number.isFinite(number)) {
    return "";
  }

  if (number === 0) {
    return "Zero";
  }

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convert = (num) => {

    if (num < 20) {
      return ones[num];
    }

    if (num < 100) {
      return (
        tens[Math.floor(num / 10)] +
        (num % 10
          ? `-${ones[num % 10]}`
          : "")
      );
    }

    if (num < 1000) {
      return (
        `${ones[Math.floor(num / 100)]} Hundred` +
        (num % 100
          ? ` And ${convert(num % 100)}`
          : "")
      );
    }

    if (num < 100000) {
      return (
        `${convert(Math.floor(num / 1000))} Thousand` +
        (num % 1000
          ? ` ${convert(num % 1000)}`
          : "")
      );
    }

    if (num < 10000000) {
      return (
        `${convert(Math.floor(num / 100000))} Lakh` +
        (num % 100000
          ? ` ${convert(num % 100000)}`
          : "")
      );
    }

    return (
      `${convert(Math.floor(num / 10000000))} Crore` +
      (num % 10000000
        ? ` ${convert(num % 10000000)}`
        : "")
    );
  };

  return convert(Math.floor(number));
};


// ==========================================
// DATE
// ==========================================

const formatDate = (date) => {

  if (!date) {
    return "";
  }

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString("en-GB");
};


// ==========================================
// SINGLE RECEIPT
// ==========================================

const ReceiptBlock = ({
  fee,
  institute,
}) => {

  const total = Number(
    fee?.totalAmount ||
    fee?.amount ||
    fee?.total ||
    0
  );

  const amountInWords =
    numberToWords(total);


  return (

    <View style={styles.receipt}>

      {/* ======================================
          HEADER
      ====================================== */}

      <View style={styles.header}>

        <Text style={styles.schoolName}>
          {institute?.instituteName ||
            "Windermere High School"}
        </Text>

        <Text style={styles.address}>
          {institute?.instituteAddress ||
            "No.13/1 III Cross Magadi Road"}
        </Text>

        <Text style={styles.city}>
          {institute?.instituteCity ||
            "Bangalore-23"}
        </Text>

      </View>


      {/* ======================================
          TITLE
      ====================================== */}

      <Text style={styles.receiptTitle}>
        FEE RECEIPT
      </Text>


      {/* ======================================
          FIRST INFORMATION ROW
      ====================================== */}

      <View style={styles.infoTable}>

        <View style={styles.infoRow}>

          <View style={styles.labelReceiptNo}>
            <Text>
              Receipt No:
            </Text>
          </View>

          <View style={styles.valueReceiptNo}>
            <Text>
              {fee?.receiptNo || "-"}
            </Text>
          </View>


          <View style={styles.labelDate}>
            <Text>
              Receipt Date:
            </Text>
          </View>

          <View style={styles.valueDate}>
            <Text>
              {formatDate(
                fee?.paymentDate ||
                fee?.date
              )}
            </Text>
          </View>


          <View style={styles.labelPrintDate}>
            <Text>
              Receipt Print Date:
            </Text>
          </View>

          <View style={styles.valuePrintDate}>
            <Text>
              {formatDate(new Date())}
            </Text>
          </View>


          <View style={styles.labelPayment}>
            <Text>
              Payment Mode:
            </Text>
          </View>

          <View style={styles.valuePayment}>
            <Text>
              {fee?.modeOfPayment || "-"}
            </Text>
          </View>

        </View>


        <View style={styles.infoRow}>

          <View style={styles.nameLabel}>
            <Text>Name:</Text>
          </View>

          <View style={styles.nameValue}>
            <Text>
              {fee?.studentName || "-"}
            </Text>
          </View>

          <View style={styles.stdLabel}>
            <Text>Std/Sec:</Text>
          </View>

          <View style={styles.stdValue}>
            <Text>
              {fee?.className || "-"}
            </Text>
          </View>

        </View>

      </View>


      {/* ======================================
          FEE TABLE
      ====================================== */}

      <View style={styles.feeTable}>

        <View style={styles.feeHeader}>

          <Text style={styles.termCell}>
            Term Fees
          </Text>

          <Text style={styles.totalCell}>
            Total
          </Text>

        </View>


        <View style={styles.feeRow}>

          <Text style={styles.termCell}>
            {total}
          </Text>

          <Text style={styles.totalCell}>
            {total}
          </Text>

        </View>

      </View>


      {/* ======================================
          AMOUNT IN WORDS
      ====================================== */}

      <View style={styles.wordsRow}>

        <Text style={styles.wordsLabel}>
          Received a sum of Rupees
        </Text>

        <Text style={styles.wordsValue}>
          {amountInWords}
        </Text>

        <Text style={styles.only}>
          only.
        </Text>

      </View>


      {/* ======================================
          SIGNATURE
      ====================================== */}

      <View style={styles.signatureContainer}>

        <Text style={styles.signature}>
          Signature
        </Text>

      </View>

    </View>

  );
};


// ==========================================
// MAIN PDF
// ==========================================

const FeeReceipt = ({
  fee,
  institute,
}) => {

  if (!fee) {
    return null;
  }


  return (

    <Document>

      <Page
        size="A4"
        style={styles.page}
      >

        {/* FIRST COPY */}

        <ReceiptBlock
          fee={fee}
          institute={institute}
        />


        {/* SECOND COPY */}

        <ReceiptBlock
          fee={fee}
          institute={institute}
        />

      </Page>

    </Document>

  );
};


// ==========================================
// STYLES
// ==========================================

const styles = StyleSheet.create({

  page: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 9,
  },


  // ========================================
  // RECEIPT
  // ========================================

  receipt: {
    height: 355,
    marginBottom: 12,
  },


  // ========================================
  // HEADER
  // ========================================

  header: {
    alignItems: "center",
    marginBottom: 5,
  },


  schoolName: {
    fontSize: 17,
    fontWeight: "bold",
    marginBottom: 5,
  },


  address: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    textAlign: "center",
  },


  city: {
    fontSize: 10,
    fontWeight: "bold",
  },


  // ========================================
  // TITLE
  // ========================================

  receiptTitle: {
    textAlign: "center",
    fontSize: 13,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 22,
  },


  // ========================================
  // INFO TABLE
  // ========================================

  infoTable: {
    width: "100%",
    borderWidth: 0.6,
    borderColor: "#333",
  },


  infoRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },


  // ----------------------------------------
  // FIRST ROW
  // ----------------------------------------

  labelReceiptNo: {
    width: "13%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  valueReceiptNo: {
    width: "12%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },


  labelDate: {
    width: "13%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  valueDate: {
    width: "12%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },


  labelPrintDate: {
    width: "16%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  valuePrintDate: {
    width: "11%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },


  labelPayment: {
    width: "14%",
    padding: 5,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  valuePayment: {
    width: "9%",
    padding: 5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },


  // ----------------------------------------
  // SECOND ROW
  // ----------------------------------------
  nameLabel: {
    width: "13%",
    padding: 5,
    borderRightWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  nameValue: {
    width: "37%",
    padding: 5,
    borderRightWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  stdLabel: {
    width: "16%",
    padding: 5,
    borderRightWidth: 0.5,
    borderColor: "#333",
    fontSize: 8.5,
  },

  stdValue: {
    width: "34%",
    padding: 5,
    fontSize: 8.5,
  },


  // ========================================
  // FEE TABLE
  // ========================================

  feeTable: {
    width: "100%",
    borderWidth: 0.6,
    borderTopWidth: 0,
    borderColor: "#333",
  },


  feeHeader: {
    flexDirection: "row",
  },


  feeRow: {
    flexDirection: "row",
  },


  termCell: {
    width: "50%",
    textAlign: "center",
    padding: 6,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 9,
    fontWeight: "bold",
  },


  totalCell: {
    width: "50%",
    textAlign: "center",
    padding: 6,
    borderBottomWidth: 0.5,
    borderColor: "#333",
    fontSize: 9,
    fontWeight: "bold",
  },


  // ========================================
  // WORDS
  // ========================================

  wordsRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginTop: 20,
    paddingHorizontal: 4,
  },


  wordsLabel: {
    width: "28%",
    fontSize: 9,
    fontWeight: "bold",
  },


  wordsValue: {
    width: "62%",
    textAlign: "center",
    fontSize: 9,
    fontWeight: "bold",
    borderBottomWidth: 0.6,
    borderBottomColor: "#333",
    paddingBottom: 4,
  },


  only: {
    width: "10%",
    textAlign: "right",
    fontSize: 9,
    fontWeight: "bold",
  },


  // ========================================
  // SIGNATURE
  // ========================================

  signatureContainer: {
    alignItems: "flex-end",
    marginTop: 65,
    paddingRight: 30,
  },


  signature: {
    fontSize: 10,
    fontStyle: "italic",
  },

});


export default FeeReceipt;